import { BasisSwitcher } from "@/components/accounting/basis-switcher";
import { PeriodSelector } from "@/components/accounting/period-selector";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import {
	useDraftEntry,
	useListAccounts,
	useListPeriods,
	usePostEntry,
} from "@/hooks/use-accounting";
import { useAccountingShortcuts } from "@/hooks/use-accounting-shortcuts";
import type {
	CellPosition,
	CellValueChangedEvent,
	ColDef,
	ICellEditorParams,
	ICellRendererParams,
	TabToNextCellParams,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useOrganizationStore } from "@/lib/organization";
import { useWorkspaceStore } from "@/lib/workspace";
import { Timestamp } from "@bufbuild/protobuf";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Building2, ChevronDown, X } from "lucide-react";
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Entry templates — stored in localStorage per workspace
// ---------------------------------------------------------------------------

interface EntryTemplate {
	name: string;
	description: string;
	lines: { accountCode: string; accountName: string; description: string }[];
	createdAt: string;
}

const TEMPLATES_KEY = "omnios-entry-templates";

function loadTemplates(workspaceId: string): EntryTemplate[] {
	try {
		const raw = localStorage.getItem(`${TEMPLATES_KEY}-${workspaceId}`);
		return raw ? JSON.parse(raw) : [];
	} catch {
		return [];
	}
}

function saveTemplates(workspaceId: string, templates: EntryTemplate[]) {
	localStorage.setItem(
		`${TEMPLATES_KEY}-${workspaceId}`,
		JSON.stringify(templates),
	);
}

export const Route = createFileRoute("/accounting/journal/new")({
	component: NewJournalEntryPage,
});

interface LineRow {
	id: number;
	accountId: string;
	accountCode: string;
	accountName: string;
	description: string;
	debit: number | null;
	credit: number | null;
}

let rowIdCounter = 0;
function nextRowId() {
	return ++rowIdCounter;
}

function emptyRow(): LineRow {
	return {
		id: nextRowId(),
		accountId: "",
		accountCode: "",
		accountName: "",
		description: "",
		debit: null,
		credit: null,
	};
}

// ---------------------------------------------------------------------------
// Account cell editor — renders a filterable dropdown inside the AG Grid cell
// ---------------------------------------------------------------------------

interface AccountEditorProps extends ICellEditorParams {
	accounts: Array<{
		id: string;
		code: string;
		name: string;
		accountType: number;
	}>;
}

const AccountCellEditor = forwardRef(
	(props: AccountEditorProps, ref: React.Ref<unknown>) => {
		const { accounts } = props;
		const [filter, setFilter] = useState(props.value ?? "");
		const [selectedIndex, setSelectedIndex] = useState(0);
		const inputRef = useRef<HTMLInputElement>(null);
		const listRef = useRef<HTMLDivElement>(null);

		const filtered = useMemo(() => {
			if (!filter) return accounts;
			const lower = filter.toLowerCase();
			return accounts.filter(
				(a) =>
					a.code.toLowerCase().includes(lower) ||
					a.name.toLowerCase().includes(lower),
			);
		}, [accounts, filter]);

		useEffect(() => {
			inputRef.current?.focus();
		}, []);

		useEffect(() => {
			setSelectedIndex(0);
		}, [filtered.length]);

		// Scroll selected item into view
		useEffect(() => {
			const list = listRef.current;
			if (!list) return;
			const item = list.children[selectedIndex] as HTMLElement | undefined;
			item?.scrollIntoView({ block: "nearest" });
		}, [selectedIndex]);

		useImperativeHandle(ref, () => ({
			getValue() {
				return filter;
			},
			isPopup() {
				return true;
			},
			getPopupPosition() {
				return "under" as const;
			},
		}));

		function selectAccount(account: (typeof accounts)[0]) {
			// Update the row data directly via the grid API
			const rowNode = props.node;
			rowNode.setDataValue("accountCode", account.code);
			rowNode.setDataValue("accountName", account.name);
			rowNode.setDataValue("accountId", account.id);
			props.stopEditing();
		}

		function handleKeyDown(e: React.KeyboardEvent) {
			if (e.key === "ArrowDown") {
				e.preventDefault();
				setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				setSelectedIndex((i) => Math.max(i - 1, 0));
			} else if (e.key === "Enter") {
				e.preventDefault();
				if (filtered[selectedIndex]) {
					selectAccount(filtered[selectedIndex]);
				}
			} else if (e.key === "Escape") {
				props.stopEditing(true);
			} else if (e.key === "Tab") {
				// Let tab propagate to move to next cell
				if (filtered[selectedIndex]) {
					selectAccount(filtered[selectedIndex]);
				}
			}
		}

		return (
			<div
				className="w-[380px] rounded-md border bg-popover shadow-lg"
				onKeyDown={handleKeyDown}
			>
				<Input
					ref={inputRef}
					value={filter}
					onChange={(e) => setFilter(e.target.value)}
					className="w-full border-b bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
					placeholder="..."
				/>
				<div ref={listRef} className="max-h-[240px] overflow-y-auto py-1">
					{filtered.length === 0 ? (
						<div className="px-3 py-2 text-xs text-muted-foreground">—</div>
					) : (
						filtered.map((account, idx) => (
							<div
								key={account.id}
								className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm ${
									idx === selectedIndex
										? "bg-accent text-accent-foreground"
										: "hover:bg-accent/50"
								}`}
								onMouseDown={(e) => {
									e.preventDefault();
									selectAccount(account);
								}}
								onMouseEnter={() => setSelectedIndex(idx)}
							>
								<span className="font-mono text-xs tabular-nums">
									{account.code}
								</span>
								<span className="truncate">{account.name}</span>
							</div>
						))
					)}
				</div>
			</div>
		);
	},
);

// ---------------------------------------------------------------------------
// Remove button cell renderer
// ---------------------------------------------------------------------------

function RemoveButtonRenderer(
	props: ICellRendererParams & { onRemove: (id: number) => void },
) {
	return (
		<Button
			type="button"
			variant="ghost"
			size="icon"
			onClick={() => props.onRemove(props.data.id)}
			className="inline-flex h-full w-full items-center justify-center text-muted-foreground hover:text-destructive"
			tabIndex={-1}
		>
			<X className="h-3.5 w-3.5" />
		</Button>
	);
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function NewJournalEntryPage() {
	const { t } = useTranslation("accounting");
	const navigate = useNavigate();
	const allEntities = useOrganizationStore((s) => s.allEntities);

	const postEntry = usePostEntry();
	const draftEntry = useDraftEntry();

	const { data: accountsData } = useListAccounts();
	const accounts = useMemo(
		() =>
			(accountsData?.accounts ?? []).map((a) => ({
				id: a.id,
				code: a.code,
				name: a.name,
				accountType: a.accountType,
			})),
		[accountsData],
	);

	const { data: periodsData } = useListPeriods();
	const periods = useMemo(
		() =>
			(periodsData?.periods ?? []).map((p) => ({
				id: p.id,
				label: p.periodId,
				status:
					p.status === 1 ? "open" : p.status === 3 ? "closed" : "soft-closed",
			})),
		[periodsData],
	);

	const [description, setDescription] = useState("");
	const [validDate, setValidDate] = useState(() => {
		const d = new Date();
		return d.toISOString().slice(0, 10);
	});
	const [periodId, setPeriodId] = useState("");
	const [basis, setBasis] = useState("accrual");
	const [currency, setCurrency] = useState("RUB");
	const [rowData, setRowData] = useState<LineRow[]>(() => [
		emptyRow(),
		emptyRow(),
		emptyRow(),
	]);

	const gridRef = useRef<AgGridReact>(null);
	// Dark mode is handled by CSS (.dark .ag-theme-quartz)

	// Auto-select first open period
	useEffect(() => {
		if (!periodId && periods.length > 0) {
			const open = periods.find((p) => p.status === "open");
			if (open) setPeriodId(open.id);
		}
	}, [periods, periodId]);

	// Totals
	const totalDebit = useMemo(
		() => rowData.reduce((s, r) => s + (r.debit ?? 0), 0),
		[rowData],
	);
	const totalCredit = useMemo(
		() => rowData.reduce((s, r) => s + (r.credit ?? 0), 0),
		[rowData],
	);
	const isBalanced = Math.abs(totalDebit - totalCredit) < 0.005;

	// Auto-balance
	const autoBalanceTarget = useMemo(() => {
		const emptyLines = rowData.filter(
			(l) => l.debit == null && l.credit == null,
		);
		if (emptyLines.length !== 1) return null;
		const filledDebit = rowData
			.filter((l) => l !== emptyLines[0])
			.reduce((s, l) => s + (l.debit ?? 0), 0);
		const filledCredit = rowData
			.filter((l) => l !== emptyLines[0])
			.reduce((s, l) => s + (l.credit ?? 0), 0);
		const diff = filledDebit - filledCredit;
		if (Math.abs(diff) < 0.005) return null;
		return {
			id: emptyLines[0].id,
			side: diff > 0 ? ("credit" as const) : ("debit" as const),
			amount: Math.round(Math.abs(diff) * 100) / 100,
		};
	}, [rowData]);

	function handleAutoBalance() {
		if (!autoBalanceTarget) return;
		setRowData((prev) =>
			prev.map((r) =>
				r.id === autoBalanceTarget.id
					? { ...r, [autoBalanceTarget.side]: autoBalanceTarget.amount }
					: r,
			),
		);
	}

	// Remove row
	const handleRemoveRow = useCallback((id: number) => {
		setRowData((prev) => {
			const next = prev.filter((r) => r.id !== id);
			return next.length === 0 ? [emptyRow()] : next;
		});
	}, []);

	// Cell value changed — enforce debit/credit mutual exclusion + auto-add row
	const onCellValueChanged = useCallback(
		(event: CellValueChangedEvent<LineRow>) => {
			const { colDef, data, newValue } = event;
			const field = colDef.field as keyof LineRow;

			setRowData((prev) => {
				let updated = prev.map((r) => {
					if (r.id !== data.id) return r;
					const row = { ...r };

					if (field === "debit" && newValue) {
						row.debit =
							newValue != null && newValue !== "" ? Number(newValue) : null;
						if (row.debit != null) row.credit = null;
					} else if (field === "credit" && newValue) {
						row.credit =
							newValue != null && newValue !== "" ? Number(newValue) : null;
						if (row.credit != null) row.debit = null;
					} else {
						(row as Record<string, unknown>)[field] = newValue;
					}

					return row;
				});

				// Auto-add a new empty row when editing the last row
				const lastRow = updated[updated.length - 1];
				if (lastRow && lastRow.id === data.id) {
					const hasContent =
						lastRow.accountCode ||
						lastRow.description ||
						lastRow.debit != null ||
						lastRow.credit != null;
					if (hasContent) {
						updated = [...updated, emptyRow()];
					}
				}

				return updated;
			});
		},
		[],
	);

	// Tab navigation: Account Code → Description → Debit → Credit → next row Account Code
	const tabToNextCell = useCallback(
		(params: TabToNextCellParams): CellPosition | null => {
			const columnOrder = ["accountCode", "description", "debit", "credit"];
			const currentCol = params.previousCellPosition.column.getId();
			const currentIdx = columnOrder.indexOf(currentCol);
			let nextRowIndex = params.previousCellPosition.rowIndex;
			let nextColIdx = currentIdx;

			if (params.backwards) {
				nextColIdx--;
				if (nextColIdx < 0) {
					nextColIdx = columnOrder.length - 1;
					nextRowIndex--;
				}
			} else {
				nextColIdx++;
				if (nextColIdx >= columnOrder.length) {
					nextColIdx = 0;
					nextRowIndex++;
				}
			}

			if (nextRowIndex < 0) return null;

			const api = gridRef.current?.api;
			if (!api) return null;

			const totalRows = api.getDisplayedRowCount();
			if (nextRowIndex >= totalRows) return null;

			const nextColId = columnOrder[nextColIdx];
			const column = api.getColumn(nextColId);
			if (!column) return null;

			return {
				rowIndex: nextRowIndex,
				column: column as unknown as CellPosition["column"],
				rowPinned: null,
			} satisfies CellPosition;
		},
		[],
	);

	// Column definitions
	const columnDefs = useMemo<ColDef<LineRow>[]>(
		() => [
			{
				headerName: "#",
				width: 44,
				maxWidth: 44,
				sortable: false,
				filter: false,
				editable: false,
				suppressNavigable: true,
				cellClass: "text-muted-foreground text-xs text-center",
				valueGetter: (params) => {
					if (!params.node) return "";
					return (params.node.rowIndex ?? 0) + 1;
				},
			},
			{
				field: "accountCode",
				headerName: t("account_code"),
				width: 120,
				editable: true,
				cellEditor: AccountCellEditor,
				cellEditorParams: { accounts },
				cellEditorPopup: true,
				cellClass: "font-mono tabular-nums text-xs",
				cellStyle: (params) =>
					!params.value
						? { color: "hsl(var(--muted-foreground))", fontStyle: "italic" }
						: undefined,
				valueFormatter: (params) => {
					if (!params.data?.accountCode) return "...";
					return params.data.accountCode;
				},
			},
			{
				field: "accountName",
				headerName: t("account_name"),
				flex: 1,
				minWidth: 180,
				editable: false,
				cellClass: "text-muted-foreground text-xs",
				valueFormatter: (params) => params.data?.accountName ?? "",
			},
			{
				field: "description",
				headerName: t("line_description"),
				flex: 1,
				minWidth: 160,
				editable: true,
				cellClass: "text-xs",
			},
			{
				field: "debit",
				headerName: t("line_debit"),
				width: 140,
				editable: true,
				cellDataType: "number",
				cellClass: "text-right font-mono tabular-nums text-xs",
				headerClass: "ag-right-aligned-header",
				valueFormatter: (params) => {
					const v = params.value;
					if (v == null || v === "") return "";
					return Number(v).toFixed(2);
				},
				valueParser: (params) => {
					if (params.newValue === "" || params.newValue == null) return null;
					const n = Number.parseFloat(params.newValue);
					return Number.isNaN(n) ? null : Math.round(n * 100) / 100;
				},
			},
			{
				field: "credit",
				headerName: t("line_credit"),
				width: 140,
				editable: true,
				cellDataType: "number",
				cellClass: "text-right font-mono tabular-nums text-xs",
				headerClass: "ag-right-aligned-header",
				valueFormatter: (params) => {
					const v = params.value;
					if (v == null || v === "") return "";
					return Number(v).toFixed(2);
				},
				valueParser: (params) => {
					if (params.newValue === "" || params.newValue == null) return null;
					const n = Number.parseFloat(params.newValue);
					return Number.isNaN(n) ? null : Math.round(n * 100) / 100;
				},
			},
			{
				headerName: "",
				width: 36,
				maxWidth: 36,
				sortable: false,
				filter: false,
				editable: false,
				suppressNavigable: true,
				cellRenderer: RemoveButtonRenderer,
				cellRendererParams: { onRemove: handleRemoveRow },
				cellClass: "p-0",
			},
		],
		[t, accounts, handleRemoveRow],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: false,
			filter: false,
			resizable: false,
			suppressMovable: true,
			suppressHeaderMenuButton: true,
		}),
		[],
	);

	// Build request for posting/drafting
	function buildLineInputs() {
		return rowData
			.filter((l) => l.accountId && (l.debit != null || l.credit != null))
			.map((l) => ({
				accountCode: l.accountCode,
				accountId: l.accountId,
				side: l.debit != null ? 1 : 2,
				amount: String(l.debit ?? l.credit ?? 0),
				currency,
				description: l.description,
			}));
	}

	function buildRequest() {
		return {
			description,
			currency,
			basis,
			validDate: validDate
				? Timestamp.fromDate(new Date(validDate))
				: undefined,
			periodId,
			category: "",
			entryType: "",
			origin: "manual",
			lines: buildLineInputs(),
		};
	}

	// ── Templates ──
	const wsId = useWorkspaceStore.getState().selectedId ?? "";
	const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
	const [loadTemplateOpen, setLoadTemplateOpen] = useState(false);
	const [templateName, setTemplateName] = useState("");
	const [templates, setTemplates] = useState<EntryTemplate[]>(() =>
		loadTemplates(wsId),
	);

	function handleSaveTemplate() {
		if (!templateName.trim()) return;
		const lines = rowData
			.filter((l) => l.accountCode)
			.map((l) => ({
				accountCode: l.accountCode,
				accountName: l.accountName,
				description: l.description,
			}));
		const tpl: EntryTemplate = {
			name: templateName.trim(),
			description,
			lines,
			createdAt: new Date().toISOString(),
		};
		const updated = [...templates, tpl];
		saveTemplates(wsId, updated);
		setTemplates(updated);
		setTemplateName("");
		setSaveTemplateOpen(false);
	}

	function handleLoadTemplate(tpl: EntryTemplate) {
		setDescription(tpl.description);
		const newRows = tpl.lines.map((l) => {
			const acct = accounts.find((a) => a.code === l.accountCode);
			return {
				...emptyRow(),
				accountId: acct?.id ?? "",
				accountCode: l.accountCode,
				accountName: acct?.name ?? l.accountName,
				description: l.description,
			};
		});
		newRows.push(emptyRow());
		setRowData(newRows);
		setLoadTemplateOpen(false);
	}

	function handleDeleteTemplate(idx: number) {
		const updated = templates.filter((_, i) => i !== idx);
		saveTemplates(wsId, updated);
		setTemplates(updated);
	}

	function handlePost() {
		postEntry.mutate(buildRequest(), {
			onSuccess: () => {
				// Reset form for next entry
				setRowData([emptyRow(), emptyRow(), emptyRow()]);
				setDescription("");
			},
		});
	}

	function handleDraft() {
		draftEntry.mutate(buildRequest(), {
			onSuccess: () => navigate({ to: "/accounting/journal" }),
		});
	}

	function handlePostAndClose() {
		postEntry.mutate(buildRequest(), {
			onSuccess: () => navigate({ to: "/accounting/journal" }),
		});
	}

	function handleKeyDown(e: React.KeyboardEvent) {
		if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
			e.preventDefault();
			if (isBalanced && totalDebit > 0) handlePost();
		}
	}

	const isBusy = postEntry.isPending || draftEntry.isPending;

	useAccountingShortcuts(
		useMemo(
			() => ({
				F9: () => {
					if (isBalanced && totalDebit > 0 && !isBusy) handlePostAndClose();
				},
				"Ctrl+Enter": () => {
					if (isBalanced && totalDebit > 0 && !isBusy) handlePost();
				},
				"Ctrl+S": () => {
					if (!isBusy) handleDraft();
				},
				Escape: () => navigate({ to: "/accounting/journal" }),
			}),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[isBalanced, totalDebit, isBusy],
		),
	);

	const balanceText = isBalanced
		? `✓ ${t("balanced")}`
		: `✗ ${t("difference")}: ${Math.abs(totalDebit - totalCredit).toFixed(2)}`;

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			...(periodId
				? [
						{
							id: "period",
							content: `${t("period")}: ${periodId}`,
							align: "left" as const,
						},
					]
				: []),
			{
				id: "basis",
				content: `${t("basis")}: ${t(`basis_${basis}`)}`,
				align: "left" as const,
			},
			{ id: "balance", content: balanceText, align: "left" as const },
		],
		[periodId, basis, balanceText, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	if (allEntities) {
		return (
			<div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-24 px-6">
				<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted mb-4">
					<Building2 className="h-6 w-6 text-muted-foreground" />
				</div>
				<p className="text-sm text-muted-foreground mb-4">
					{t("select_entity_to_create")}
				</p>
				<Link
					to="/accounting/journal"
					className="text-sm text-primary hover:underline"
				>
					{t("journal_entries")}
				</Link>
			</div>
		);
	}

	return (
		<div className="flex flex-col h-full" onKeyDown={handleKeyDown}>
			{/* Header bar */}
			<div className="flex items-center gap-3 px-3 py-2 border-b bg-muted/30">
				<div className="flex-1 min-w-[200px]">
					<Input
						id="entry-desc"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						placeholder={t("entry_description")}
						className="h-8 text-sm bg-background"
					/>
				</div>
				<Input
					id="entry-date"
					type="date"
					value={validDate}
					onChange={(e) => setValidDate(e.target.value)}
					className="h-8 w-[130px] bg-background text-sm font-mono"
				/>
				<PeriodSelector
					value={periodId}
					onChange={setPeriodId}
					periods={periods}
					className="w-[140px]"
				/>
				<BasisSwitcher value={basis} onChange={setBasis} />
				<Input
					id="entry-currency"
					value={currency}
					onChange={(e) => setCurrency(e.target.value.toUpperCase())}
					maxLength={3}
					className="h-8 w-[60px] bg-background font-mono text-sm text-center"
				/>
				{autoBalanceTarget && (
					<Button
						variant="outline"
						size="sm"
						onClick={handleAutoBalance}
						className="h-8 text-xs shrink-0"
					>
						{t("auto_balance")}
					</Button>
				)}
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline" size="sm" className="h-9">
							{t("toolbar_more")}
							<ChevronDown className="h-3.5 w-3.5 ml-1 opacity-50" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={() => setSaveTemplateOpen(true)}>
							{t("action_save_template")}
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() => {
								setTemplates(loadTemplates(wsId));
								setLoadTemplateOpen(true);
							}}
						>
							{t("action_from_template")}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={handleDraft}
					disabled={isBusy}
				>
					{t("toolbar_save_draft")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={handlePost}
					disabled={isBusy || !isBalanced || totalDebit === 0}
				>
					{t("toolbar_post")}
				</Button>
				<Button
					size="sm"
					className="h-9"
					onClick={handlePostAndClose}
					disabled={isBusy || !isBalanced || totalDebit === 0}
				>
					{t("toolbar_post_and_close")}
				</Button>
			</div>

			{/* AG Grid lines — fills remaining space */}
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
				<AgGridReact<LineRow>
					ref={gridRef}
					rowData={rowData}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					getRowId={(params) => String(params.data.id)}
					singleClickEdit
					stopEditingWhenCellsLoseFocus
					onCellValueChanged={onCellValueChanged}
					tabToNextCell={tabToNextCell}
					headerHeight={30}
					rowHeight={30}
					suppressCellFocus={false}
					animateRows={false}
				/>
			</div>

			{/* Totals footer */}
			<div className="flex items-center px-3 py-1.5 border-t bg-muted/30 text-xs font-semibold">
				<span className="w-[44px]" />
				<span className="flex-1">
					{isBalanced && totalDebit > 0
						? t("balanced")
						: totalDebit === 0 && totalCredit === 0
							? t("empty_journal_desc", { defaultValue: "Кликните на ячейку «Код», чтобы начать ввод" })
							: `${t("difference")}: ${Math.abs(totalDebit - totalCredit).toFixed(2)}`}
				</span>
				<span className="w-[140px] text-right font-mono tabular-nums">
					{totalDebit > 0 ? totalDebit.toFixed(2) : ""}
				</span>
				<span className="w-[140px] text-right font-mono tabular-nums">
					{totalCredit > 0 ? totalCredit.toFixed(2) : ""}
				</span>
				<span className="w-[36px]" />
			</div>

			{/* Save Template dialog */}
			<Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action_save_template")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-3">
						<div className="space-y-1.5">
							<Label>{t("template_name_label")}</Label>
							<Input
								value={templateName}
								onChange={(e) => setTemplateName(e.target.value)}
								placeholder={t("template_name_placeholder")}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleSaveTemplate();
								}}
								autoFocus
							/>
						</div>
						<p className="text-xs text-muted-foreground">
							{t("template_save_hint", {
								count: rowData.filter((l) => l.accountCode).length,
								defaultValue: `${rowData.filter((l) => l.accountCode).length} строк будет сохранено в шаблон`,
							})}
						</p>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setSaveTemplateOpen(false)}
						>
							{t("cancel")}
						</Button>
						<Button
							onClick={handleSaveTemplate}
							disabled={!templateName.trim()}
						>
							{t("save")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Load Template dialog */}
			<Dialog open={loadTemplateOpen} onOpenChange={setLoadTemplateOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action_from_template")}</DialogTitle>
					</DialogHeader>
					{templates.length === 0 ? (
						<p className="text-sm text-muted-foreground py-4 text-center">
							{t("no_templates", {
								defaultValue: "Нет сохранённых шаблонов",
							})}
						</p>
					) : (
						<div className="divide-y max-h-64 overflow-y-auto">
							{templates.map((tpl, idx) => (
								<div
									key={tpl.createdAt}
									className="flex items-center gap-2 py-2"
								>
									<Button
										type="button"
										variant="ghost"
										onClick={() => handleLoadTemplate(tpl)}
										className="flex-1 text-left hover:bg-accent/50 rounded px-2 py-1 transition-colors"
									>
										<p className="text-sm font-medium">{tpl.name}</p>
										<p className="text-xs text-muted-foreground">
											{tpl.lines.length}{" "}
											{t("template_lines", {
												defaultValue: "строк",
											})}
											{tpl.description && ` · ${tpl.description}`}
										</p>
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="icon"
										onClick={() => handleDeleteTemplate(idx)}
										className="text-muted-foreground hover:text-destructive p-1 rounded"
									>
										<X className="h-3.5 w-3.5" />
									</Button>
								</div>
							))}
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
